/**
 * "Comece por aqui" (22/10) — docs/designs/comece-por-aqui.md.
 *
 * Substitui o "Por onde começar?" do antigo modal de boas-vindas. A trilha
 * muda com o plano: quem tem o Completo começa conectando o banco, e o resto
 * vem dele; quem tem o Essencial cadastra à mão o que o banco traria.
 *
 * Função pura: o que está feito vem do servidor (`GET /api/comece-aqui`), e
 * nada aqui se marca por clique.
 */
import type { IdDoPlano } from '../services/api';

export interface FeitosDoComece {
  banco: boolean;
  whatsapp: boolean;
  push: boolean;
  lembretes: boolean;
  metodos: boolean;
  casa: boolean;
  ultimoMes: boolean;
  diaADia: boolean;
  finn: boolean;
}

export type Trilha = 'completo' | 'essencial';

export type AcaoDoPasso =
  | 'banco' | 'whatsapp' | 'instalar' | 'lembretes' | 'metodos' | 'casa' | 'lancar' | 'finn';

export interface PassoDoComece {
  id: keyof FeitosDoComece;
  titulo: string;
  texto: string;
  botao: string;
  acao: AcaoDoPasso;
  feito: boolean;
  /** Não conta para "tudo feito": nem toda pessoa mora com alguém. */
  opcional?: boolean;
}

export const PERGUNTA_AO_FINN = 'Faz uma análise do meu mês: onde eu posso melhorar?';

/**
 * Qual trilha. Pelo plano quando há; sem plano (a dona, a demo, o app sem
 * cobrança), pelo que ela tem: banco conectado é a trilha do Completo.
 */
export const trilhaDe = (plano: IdDoPlano | null | undefined, feitos: Pick<FeitosDoComece, 'banco'>): Trilha => {
  if (plano === 'completo') return 'completo';
  if (plano === 'essencial' || plano === 'essencial_anual') return 'essencial';
  return feitos.banco ? 'completo' : 'essencial';
};

type Molde = Omit<PassoDoComece, 'feito'>;

const BANCO: Molde = {
  id: 'banco', acao: 'banco', botao: 'Conectar banco',
  titulo: 'Conecte o seu banco',
  texto: 'Pelo Open Finance, os seus gastos e recebimentos chegam sozinhos. É daqui que vem o resto.',
};
const WHATSAPP: Molde = {
  id: 'whatsapp', acao: 'whatsapp', botao: 'Conectar WhatsApp',
  titulo: 'Conecte o WhatsApp',
  texto: 'Lance um gasto mandando uma mensagem, e pergunte do seu dinheiro sem abrir o app.',
};
const INSTALAR: Molde = {
  id: 'push', acao: 'instalar', botao: 'Baixar o app',
  titulo: 'Baixe o app e ligue os avisos',
  texto: 'É pelo app no celular que chegam os lembretes de vencimento e os avisos do Finn.',
};
const LEMBRETES: Molde = {
  id: 'lembretes', acao: 'lembretes', botao: 'Ir para lembretes',
  titulo: 'Cadastre os seus lembretes',
  texto: 'Aluguel, cartão, escola: a gente avisa antes de vencer.',
};
const METODOS: Molde = {
  id: 'metodos', acao: 'metodos', botao: 'Ir para métodos',
  titulo: 'Cadastre como você paga',
  texto: 'Cartões, conta, Pix. Com o limite do cartão, o app avisa antes de estourar.',
};
const CASA: Molde = {
  id: 'casa', acao: 'casa', botao: 'Ir para Nossa conta', opcional: true,
  titulo: 'Mora com alguém? Traga para a sua casa',
  texto: 'Vocês dividem as contas da casa, e o que é pessoal de cada um continua só seu.',
};
const ULTIMO_MES: Molde = {
  id: 'ultimoMes', acao: 'lancar', botao: 'Lançar',
  titulo: 'Coloque as contas do último mês',
  texto: 'Assim a gente vai te conhecendo. Dá para escanear ou colar o comprovante. Conta mensal e parcelada você coloca uma vez só, que ela se repete.',
};
const DIA_A_DIA: Molde = {
  id: 'diaADia', acao: 'lancar', botao: 'Lançar',
  titulo: 'Lance conforme recebe ou gasta',
  texto: 'Um pouco por dia, pelo app ou pelo WhatsApp. Se esquecer, a gente lembra.',
};
const FINN: Molde = {
  id: 'finn', acao: 'finn', botao: 'Perguntar ao Finn',
  titulo: 'Peça ao Finn uma análise do seu mês',
  texto: 'Ele olha os seus números e diz onde dá para melhorar.',
};

const TRILHAS: Record<Trilha, Molde[]> = {
  completo: [BANCO, WHATSAPP, INSTALAR, LEMBRETES, CASA, FINN],
  essencial: [WHATSAPP, INSTALAR, METODOS, LEMBRETES, CASA, ULTIMO_MES, DIA_A_DIA, FINN],
};

export const passosDoComece = (trilha: Trilha, feitos: FeitosDoComece): PassoDoComece[] =>
  TRILHAS[trilha].map(m => ({ ...m, feito: feitos[m.id] }));

/** O primeiro obrigatório que falta, na ordem da trilha. `null` = tudo feito. */
export const proximoPasso = (passos: PassoDoComece[]): PassoDoComece | null =>
  passos.find(p => !p.feito && !p.opcional) ?? null;

/** Quantos dos obrigatórios estão feitos. O opcional feito conta como bônus, nunca como falta. */
export const progresso = (passos: PassoDoComece[]): { feitos: number; total: number; completo: boolean } => {
  const obrigatorios = passos.filter(p => !p.opcional);
  const feitos = obrigatorios.filter(p => p.feito).length;
  return { feitos, total: obrigatorios.length, completo: feitos === obrigatorios.length };
};
